/**
 * Dashboard — V2.6 "Headline Newsroom" rewrite (Worker-B 2026-04-20).
 *
 * The 5-equal-metric-tile grid is replaced by a newsroom front-page
 * layout: one dominating HeadlineHero ("$627,618 · Revenue · up 17.3%
 * YoY"), three StoryCards (biggest moment / top ride / watch), a
 * compact metric strip below, then the hourly area chart secondary,
 * then the existing PG extras + drill-down panes.
 *
 * The step-change rule: Brian should say "this is different" in 3
 * seconds on first paint. The eye lands on one number, not five.
 *
 * Reads from `/api/dashboard/:eventId/stats`, `/compare`, and `/extras`
 * (the latter mounted via existing components when `source === 'pg'`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { RefreshCw } from 'lucide-react'
import { readDashboardCache, writeDashboardCache } from '@/lib/dashboardCache'
import {
  isDashboardAnomaliesEnabled,
  isDashboardExplainPanelEnabled,
  isDashboardTimeWindowEnabled,
} from '@/lib/dashboardFlags'
import { DashboardExtrasPane } from './DashboardExtras'
import { DashboardDrillDown } from './DashboardDrillDown'
import { HeadlineHero } from './dashboard/HeadlineHero'
import { StoryCard } from './dashboard/StoryCard'
import { CompactMetricStrip } from './dashboard/CompactMetricStrip'
import { AnomalyRail } from './dashboard/AnomalyRail'
import type { Anomaly } from './dashboard/AnomalyChip'
import {
  ExplainPanel,
  type ExplainPanelTarget,
} from './dashboard/ExplainPanel'
import { DashboardToolbar } from './dashboard/DashboardToolbar'
import { TimeWindowSelect } from './dashboard/TimeWindowSelect'

interface HourlyPoint {
  hour: number
  orders: number
  revenue: number
  riders: number
}

interface DashboardStats {
  // 'mock'      → hardcoded demo numbers (default, no butler1 reachable)
  // 'pg'        → future rollups path from b1adb Postgres cache
  // 'mm_bridge' → live butler1 via Python mm_bridge.py Tier A/B/C composite
  //               (DASHBOARD_LIVE=1 on server). See dashboard-stats.js.
  source: 'mock' | 'pg' | 'mm_bridge'
  event: {
    event_id: number
    event_name: string
    event_date: string
    status: string
  }
  metrics: {
    revenue_total: number
    ride_gross: number
    guests: number
    riders_per_hour: number
    orders_per_hour: number
  }
  hourly: HourlyPoint[]
  generated_at: string
}

const fmtUSD = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)

const fmtUSDShort = (n: number): string => {
  if (!Number.isFinite(n)) return '—'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  return fmtUSD(n)
}

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n)

const fmtHour = (h: number) => {
  if (h === 0) return '12am'
  if (h === 12) return '12pm'
  return h > 12 ? `${h - 12}pm` : `${h}am`
}

function signedUSD(n: number): string {
  const sign = n >= 0 ? '+' : '−'
  return `${sign}${fmtUSD(Math.abs(n))}`
}

// ─── Pure derivations for StoryCards ──────────────────────────────────────

interface PeakHour {
  hour: number
  revenue: number
  orders: number
}

/**
 * Pick the hour with the highest revenue. Returns null when there's no
 * usable data — caller renders an empty-tone card instead of fabricating.
 */
export function derivePeakHour(hourly: HourlyPoint[] | undefined | null): PeakHour | null {
  if (!Array.isArray(hourly) || hourly.length === 0) return null
  let best: PeakHour | null = null
  for (const h of hourly) {
    if (!h || !Number.isFinite(h.revenue) || h.revenue <= 0) continue
    if (!best || h.revenue > best.revenue) {
      best = { hour: h.hour, revenue: h.revenue, orders: h.orders || 0 }
    }
  }
  return best
}

interface WatchHour {
  hour: number
  revenue: number
  averageRevenue: number
}

/**
 * Find an interior hour whose revenue is dramatically below the mean of
 * its adjacent neighbors — a true local soft-patch, not the natural
 * trough at open/close of a bell-curve day.
 *
 * Codex 2026-04-20 residual HIGH (V2.6 Worker-B pass 2): prior
 * implementation compared each hour to the whole-event mean, which
 * falsely flagged legitimate opening/closing hours on smooth bell-curve
 * days. The fix: compare each non-edge hour to the mean of its
 * immediate neighbors and only flag if the gap exceeds 50%.
 *
 * Returns the most extreme anomaly (lowest ratio of revenue /
 * neighbor-avg) or null when the day is smooth.
 */
export function deriveWatchHour(
  hourly: HourlyPoint[] | undefined | null,
): WatchHour | null {
  if (!Array.isArray(hourly) || hourly.length < 4) return null
  // Work against an ascending copy so neighbor lookups are order-stable
  // regardless of how the payload was assembled server-side.
  const ordered = hourly
    .filter((h) => h && Number.isFinite(h.revenue) && h.revenue >= 0)
    .slice()
    .sort((a, b) => a.hour - b.hour)
  if (ordered.length < 4) return null

  let best: { pick: WatchHour; ratio: number } | null = null
  // Skip first and last points — they have only one neighbor each and
  // represent open/close troughs, not anomalies.
  for (let i = 1; i < ordered.length - 1; i++) {
    const cur = ordered[i]
    const prev = ordered[i - 1]
    const next = ordered[i + 1]
    const neighborAvg = (prev.revenue + next.revenue) / 2
    if (!(neighborAvg > 0)) continue
    const ratio = cur.revenue / neighborAvg
    // 50% gap = the anomaly is at least twice as soft as its neighbors.
    if (ratio < 0.5) {
      if (best === null || ratio < best.ratio) {
        best = {
          pick: { hour: cur.hour, revenue: cur.revenue, averageRevenue: neighborAvg },
          ratio,
        }
      }
    }
  }
  return best ? best.pick : null
}

// ─── Loading skeleton ─────────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <section
      data-testid="dashboard-skeleton"
      aria-busy="true"
      aria-label="Loading dashboard"
      className="px-4 py-5"
    >
      <header className="mb-4">
        <div className="h-3 w-40 animate-pulse rounded bg-hairline dark:bg-slate-700" />
        <div className="mt-2 h-7 w-72 animate-pulse rounded bg-hairline dark:bg-slate-700" />
        <div className="mt-2 h-3 w-48 animate-pulse rounded bg-hairline dark:bg-slate-700" />
      </header>
      <HeadlineHero label="Revenue" value="—" loading />
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="rounded-card border border-hairline bg-white p-4 shadow-card-soft dark:border-slate-700 dark:bg-slate-800"
          >
            <div className="h-3 w-16 animate-pulse rounded bg-hairline dark:bg-slate-700" />
            <div className="mt-3 h-7 w-24 animate-pulse rounded bg-hairline dark:bg-slate-700" />
            <div className="mt-2 h-3 w-20 animate-pulse rounded bg-hairline dark:bg-slate-700" />
          </div>
        ))}
      </div>
      <div className="mt-5 h-56 animate-pulse rounded-card bg-gradient-to-br from-hairline to-surface-stripe dark:from-slate-700 dark:to-slate-800" />
    </section>
  )
}

// ─── Props ────────────────────────────────────────────────────────────────

interface DashboardProps {
  /**
   * Event to load. REQUIRED — must be derived from the authenticated user's
   * tenant context (Codex review 2026-04-13 flagged the previous hardcoded
   * default `159` as HIGH because it made KPI exposure deterministic).
   * Pass `null` to render the auth-required placeholder instead of fetching.
   */
  eventId: number | null
  /**
   * Optional Bearer token for the post-login session. Codex HIGH #3
   * migration (2026-04-14) moved the canonical auth transport to the
   * HttpOnly butler_session cookie, which the browser auto-attaches on
   * same-origin fetches via `credentials: 'same-origin'`. Callers that
   * still hold a token from a legacy sessionStorage login path can
   * pass it as a fallback and it will be added as a Bearer header; new
   * integrations should pass nothing and rely on the cookie.
   */
  authToken?: string | null
}

export function Dashboard({ eventId, authToken }: DashboardProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // `isStale` is true when the rendered stats came from the sessionStorage
  // cache and a background refresh is in flight — distinct from the
  // `loading` blank-canvas state so we can render numbers immediately.
  const [isStale, setIsStale] = useState(false)
  // V2.5-B: YoY pct map from /api/dashboard/:eventId/compare. 404-tolerant:
  // a missing endpoint just leaves the chips off (same precedent as
  // AnalyticsPage). Clears on event switch.
  type YoyMap = {
    revenue_total?: number
    ride_gross?: number
    guests?: number
    riders_per_hour?: number
    orders_per_hour?: number
  }
  const [yoy, setYoy] = useState<YoyMap | null>(null)
  // V2.5-B: client-side time-window filter for the area chart. Does NOT
  // re-fetch /stats — just slices the already-fetched hourly[] array.
  type HourWindow = 'all' | 'morning' | 'afternoon' | 'evening'
  const [hourWindow, setHourWindow] = useState<HourWindow>('all')

  // V3 Page 01 — feature-flag resolutions (default OFF per fork hard-stops).
  // useMemo so the flags stay stable across renders; tests assert per-flag
  // behaviour by setting VITE_DASHBOARD_* at render time.
  const flags = useMemo(
    () => ({
      explainPanel: isDashboardExplainPanelEnabled(),
      anomalies: isDashboardAnomaliesEnabled(),
      timeWindow: isDashboardTimeWindowEnabled(),
    }),
    [],
  )

  // V3 Page 01 — ExplainPanel state. `target` is the widget spec + data
  // snapshot we pass to /api/dashboard/explain. Closed by default.
  const [explainTarget, setExplainTarget] = useState<ExplainPanelTarget | null>(null)
  // Navigation — sidesteps react-router's useNavigate() hook (which throws
  // when the component is mounted outside a Router, e.g. the V2.6
  // Dashboard.test.tsx suite). Tap-through happens via pushState so the
  // real <BrowserRouter> still picks up the route change in-app. Tests
  // that assert on navigation spy on window.history.pushState.
  const navigate = useCallback((to: string) => {
    if (typeof window === 'undefined') return
    window.history.pushState({}, '', to)
    // Trigger React Router + other listeners.
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [])

  const openExplainPanel = useCallback((target: ExplainPanelTarget) => {
    if (!flags.explainPanel) return
    setExplainTarget(target)
  }, [flags.explainPanel])

  const closeExplainPanel = useCallback(() => setExplainTarget(null), [])

  const handleAnomalyExplain = useCallback((anomaly: Anomaly) => {
    if (!flags.explainPanel) return
    setExplainTarget({
      widget: {
        id: `anomaly-${anomaly.kind}-${anomaly.hour || ''}`,
        kind: 'anomaly',
        label: anomaly.label,
      },
      data: anomaly as unknown as Record<string, unknown>,
    })
  }, [flags.explainPanel])

  useEffect(() => {
    if (eventId == null) {
      setStats(null)
      setError(null)
      setLoading(false)
      setIsStale(false)
      return
    }
    let cancelled = false
    const cached = readDashboardCache<DashboardStats>(eventId)
    if (cached) {
      setStats(cached)
      setLoading(false)
      setIsStale(true)
    } else {
      setStats(null)
      setLoading(true)
      setIsStale(false)
    }
    setError(null)

    const headers: Record<string, string> = {}
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`
    }
    fetch(`/api/dashboard/${eventId}/stats`, {
      headers,
      credentials: 'same-origin',
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data: DashboardStats) => {
        if (cancelled) return
        setStats(data)
        setLoading(false)
        setIsStale(false)
        writeDashboardCache(eventId, data)
      })
      .catch((err) => {
        if (cancelled) return
        // If we already painted a cached snapshot, leave it — refresh
        // failure shouldn't blank the screen. Surface the error only when
        // we have nothing to show.
        if (!cached) {
          setError(err instanceof Error ? err.message : 'failed to load')
        }
        setLoading(false)
        setIsStale(false)
      })
    return () => {
      cancelled = true
    }
  }, [eventId, authToken])

  // V2.5-B: YoY compare fetch. 404-tolerant per AnalyticsPage precedent —
  // if the endpoint isn't mounted or returns 4xx/5xx we silently drop the
  // chips rather than break the whole dashboard.
  useEffect(() => {
    setYoy(null)
    if (eventId == null) return
    const controller = new AbortController()
    let cancelled = false
    const headers: Record<string, string> = {}
    if (authToken) headers.Authorization = `Bearer ${authToken}`
    fetch(`/api/dashboard/${eventId}/compare`, {
      headers,
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<{ yoy_pct?: YoyMap }>
      })
      .then((data) => {
        if (cancelled) return
        setYoy(data && data.yoy_pct ? data.yoy_pct : null)
      })
      .catch((err) => {
        if (cancelled) return
        if (err && (err as { name?: string }).name === 'AbortError') return
        setYoy(null)
      })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [eventId, authToken])

  // Headline sparkline — last 12 hours of revenue.
  const headlineSparkline = useMemo(() => {
    if (!stats || !stats.hourly || stats.hourly.length === 0) return []
    return stats.hourly.slice(-12).map((h) => ({ value: h.revenue || 0 }))
  }, [stats])

  // Story card inputs.
  const peakHour = useMemo(() => derivePeakHour(stats?.hourly), [stats])
  const watchHour = useMemo(() => deriveWatchHour(stats?.hourly), [stats])

  // V2.5-B: client-side filtered hourly array for the area chart.
  const filteredHourly = useMemo(() => {
    if (!stats || !stats.hourly) return [] as HourlyPoint[]
    if (hourWindow === 'all') return stats.hourly
    return stats.hourly.filter((h) => {
      if (hourWindow === 'morning') return h.hour < 14
      if (hourWindow === 'afternoon') return h.hour >= 14 && h.hour < 18
      if (hourWindow === 'evening') return h.hour >= 18
      return true
    })
  }, [stats, hourWindow])

  if (eventId == null) {
    return (
      <section className="px-4 py-6">
        <div className="mm-card p-5 text-sm text-ink-soft dark:text-slate-400">
          <span className="marquee-dot mr-2 align-middle" />
          Sign in to see today's event metrics.
        </div>
      </section>
    )
  }

  if (loading && !stats) {
    return <DashboardSkeleton />
  }

  if (error || !stats) {
    return (
      <div className="px-4 py-6 text-sm text-marquee-700 dark:text-marquee-300">
        Couldn't load dashboard: {error ?? 'no data'}
      </div>
    )
  }

  // ─── Headline derivation ────────────────────────────────────────────────
  const revenueYoy = yoy?.revenue_total ?? null
  const revenueTotal = stats.metrics.revenue_total
  // Reconstruct prior-year revenue from pct for a concrete delta sentence
  // when yoy data is available. No fabrication — pure arithmetic from the
  // two numbers we already have.
  const priorYearRevenue =
    typeof revenueYoy === 'number' && Number.isFinite(revenueYoy) && revenueYoy > -100
      ? revenueTotal / (1 + revenueYoy / 100)
      : null
  const revenueDelta =
    priorYearRevenue !== null && Number.isFinite(priorYearRevenue)
      ? revenueTotal - priorYearRevenue
      : null
  const totalOrders = stats.hourly?.reduce((acc, h) => acc + (h.orders || 0), 0) || 0
  const deltaSegments: string[] = []
  if (revenueDelta !== null) {
    deltaSegments.push(`${signedUSD(revenueDelta)} vs prior year`)
  }
  if (totalOrders > 0) {
    deltaSegments.push(`${fmtNum(totalOrders)} orders`)
  }
  const deltaLabel = deltaSegments.length > 0 ? deltaSegments.join(' · ') : null

  // ─── Story cards ────────────────────────────────────────────────────────
  const peakHourCard = peakHour
    ? {
        title: 'Biggest Moment',
        primaryValue: fmtHour(peakHour.hour),
        primaryLabel: fmtUSD(peakHour.revenue),
        supporting:
          peakHour.orders > 0
            ? `${fmtNum(peakHour.orders)} orders in one hour`
            : 'Peak revenue hour',
        tone: 'default' as const,
      }
    : null

  const watchCard = watchHour
    ? {
        title: 'Watch',
        primaryValue: fmtHour(watchHour.hour),
        primaryLabel: fmtUSD(watchHour.revenue),
        supporting: `Soft vs ${fmtUSDShort(watchHour.averageRevenue)} avg hour`,
        tone: 'watch' as const,
      }
    : null

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <section className="px-4 py-5">
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-butler-600 dark:text-butler-200">
            Butler Amusements · Live Event
          </div>
          <h2 className="mt-1 font-display text-2xl font-bold text-butler-800 dark:text-butler-100 tracking-tight">
            {stats.event.event_name}
          </h2>
          <p className="mt-1 text-xs text-ink-soft dark:text-slate-400 flex items-center gap-2">
            <span>{stats.event.event_date}</span>
            <span className="text-hairline-strong">·</span>
            <span
              className={
                stats.event.status === 'live'
                  ? 'inline-flex items-center gap-1 font-semibold text-midway-600 dark:text-midway-300'
                  : 'text-ink-soft dark:text-slate-400'
              }
            >
              {stats.event.status === 'live' ? (
                <>
                  <span className="marquee-dot" />
                  Live now
                </>
              ) : (
                stats.event.status
              )}
            </span>
          </p>
        </div>
        {(isStale || loading) && (
          <span
            data-testid="dashboard-stale-chip"
            className="inline-flex items-center gap-1.5 rounded-full border border-glow-300 bg-glow-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-butler-700 dark:border-glow-700 dark:bg-glow-900/40 dark:text-glow-200"
            title="Showing cached data while a refresh is in flight"
          >
            <RefreshCw className="h-3 w-3 animate-spin" aria-hidden="true" />
            Refreshing
          </span>
        )}
        {stats.source === 'mm_bridge' ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-midway-500/40 bg-midway-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-midway-700 dark:bg-midway-900/40 dark:text-midway-200"
            title="Live butler1 data via mm_bridge composite"
          >
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-midway-500" aria-hidden="true" />
            Live · butler1
          </span>
        ) : stats.source === 'pg' ? (
          <span
            className="rounded-full border border-butler-200 bg-butler-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-butler-700 dark:bg-butler-900/40 dark:text-butler-200"
            title="Rollup cache from b1adb Postgres"
          >
            Cached · b1adb
          </span>
        ) : (
          <span
            data-testid="dashboard-source-demo"
            className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-glow-700 bg-glow-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-glow-800 dark:border-glow-500 dark:bg-glow-900/40 dark:text-glow-100"
            title="Demo data — DASHBOARD_LIVE not set on the server"
          >
            <span
              aria-hidden="true"
              className="inline-block h-1.5 w-1.5 rounded-full bg-glow-600 dark:bg-glow-300"
            />
            Demo mode
          </span>
        )}
      </header>

      {/* Headline — one number owns the fold.
          When DASHBOARD_EXPLAIN_PANEL is on, the hero is wrapped in a
          button that opens the ExplainPanel. V2.6 tests assert on the
          inner HeadlineHero so wrapping doesn't break them. */}
      {flags.explainPanel ? (
        <button
          type="button"
          data-testid="dashboard-headline-tap-through"
          aria-label="Explain revenue"
          onClick={() =>
            openExplainPanel({
              widget: { id: 'headline-revenue', kind: 'headline_hero', label: 'Revenue' },
              data: {
                primary_value: fmtUSD(revenueTotal),
                yoy_pct: revenueYoy ?? null,
                delta_label: deltaLabel,
              },
            })
          }
          className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-butler-400 rounded-card"
        >
          <HeadlineHero
            label="Revenue"
            value={fmtUSD(revenueTotal)}
            deltaLabel={deltaLabel}
            yoyPct={revenueYoy}
            sparkline={headlineSparkline}
            data-testid="dashboard-tile-revenue"
          />
        </button>
      ) : (
        <HeadlineHero
          label="Revenue"
          value={fmtUSD(revenueTotal)}
          deltaLabel={deltaLabel}
          yoyPct={revenueYoy}
          sparkline={headlineSparkline}
          data-testid="dashboard-tile-revenue"
        />
      )}

      {/* V3 Page 01 — AnomalyRail. Hidden when the flag or the server
          route is off. Component itself owns its fetch + dismiss state. */}
      {flags.anomalies && eventId != null ? (
        <AnomalyRail
          eventId={eventId}
          authToken={authToken ?? null}
          onExplain={handleAnomalyExplain}
        />
      ) : null}

      {/* Three story cards below the headline. Each is a concrete number;
          we never fabricate — cards that can't be derived from /stats
          collapse to an empty-tone placeholder. */}
      <div
        data-testid="dashboard-story-grid"
        className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3"
      >
        {(() => {
          // V3: when the explain-panel flag is on, StoryCards tap through
          // to the matching /analytics/template/:name/:eventId route. V2.6
          // renders them as pure tiles (no nav), preserving existing tests.
          const enableTap = flags.explainPanel && eventId != null
          const wrap = (child: React.ReactNode, route: string, label: string) =>
            enableTap ? (
              <button
                type="button"
                data-testid={`${label}-tap-through`}
                aria-label={`Open analytics template: ${label}`}
                onClick={() => navigate(route)}
                className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-butler-400 rounded-card"
              >
                {child}
              </button>
            ) : (
              <>{child}</>
            )
          const peakChild = peakHourCard ? (
            <StoryCard
              title={peakHourCard.title}
              primaryValue={peakHourCard.primaryValue}
              primaryLabel={peakHourCard.primaryLabel}
              supporting={peakHourCard.supporting}
              tone={peakHourCard.tone}
              data-testid="dashboard-story-peak"
            />
          ) : (
            <StoryCard
              title="Biggest Moment"
              primaryValue="—"
              supporting="Hourly data not available"
              tone="empty"
              data-testid="dashboard-story-peak"
            />
          )
          const topRideChild = (
            <StoryCard
              title="Top Ride"
              primaryValue="Coming next"
              supporting="Attraction leaderboard wiring up in Deep details"
              tone="empty"
              data-testid="dashboard-story-top-ride"
            />
          )
          const watchChild = watchCard ? (
            <StoryCard
              title={watchCard.title}
              primaryValue={watchCard.primaryValue}
              primaryLabel={watchCard.primaryLabel}
              supporting={watchCard.supporting}
              tone={watchCard.tone}
              data-testid="dashboard-story-watch"
            />
          ) : (
            <StoryCard
              title="Watch"
              primaryValue="All steady"
              supporting="No soft-patch hours detected"
              tone="empty"
              data-testid="dashboard-story-watch"
            />
          )
          return (
            <>
              {wrap(
                peakChild,
                `/analytics/template/event-post-mortem/${eventId}`,
                'dashboard-story-peak',
              )}
              {wrap(
                topRideChild,
                `/analytics/template/event-post-mortem/${eventId}`,
                'dashboard-story-top-ride',
              )}
              {wrap(
                watchChild,
                `/analytics/template/daily-live/${eventId}`,
                'dashboard-story-watch',
              )}
            </>
          )
        })()}
      </div>

      {/* Compact secondary metrics — Ride Gross, Guests, Riders/hr, Orders/hr.
          YoY chips ride inline so the strip stays honest about deltas
          without repeating the headline number. */}
      <CompactMetricStrip
        items={[
          {
            key: 'ride-gross',
            label: 'Ride Gross',
            value: fmtUSD(stats.metrics.ride_gross),
            yoyPct: yoy?.ride_gross ?? null,
            'data-testid': 'dashboard-tile-ride-gross',
          },
          {
            key: 'guests',
            label: 'Guests',
            value: fmtNum(stats.metrics.guests),
            yoyPct: yoy?.guests ?? null,
            'data-testid': 'dashboard-tile-guests',
          },
          {
            key: 'riders',
            label: 'Riders / hr',
            value: fmtNum(stats.metrics.riders_per_hour),
            'data-testid': 'dashboard-tile-riders',
          },
          {
            key: 'orders',
            label: 'Orders / hr',
            value: fmtNum(stats.metrics.orders_per_hour),
            'data-testid': 'dashboard-tile-orders',
          },
        ]}
      />

      {/* V2.5-B: client-side time-window filter for the area chart.
          Filter changes NEVER re-fetch /stats — they only slice the
          already-fetched hourly[] array. V3 Page 01: when
          DASHBOARD_TIME_WINDOW is on, we render the same chips through
          DashboardToolbar + TimeWindowSelect so date-range controls can
          be added alongside. The V2.5-B data-testids are preserved on
          both paths so existing selectors keep working. */}
      {flags.timeWindow ? (
        <DashboardToolbar title="Revenue · hourly">
          <TimeWindowSelect
            hourWindow={hourWindow}
            onHourWindowChange={(next) => setHourWindow(next)}
            data-testid="dashboard-filter"
          />
        </DashboardToolbar>
      ) : (
        <div
          data-testid="dashboard-filter-bar"
          className="mt-5 flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft dark:text-slate-400"
        >
          <span>Revenue · hourly</span>
          <span className="text-hairline-strong">·</span>
          {(
            [
              { key: 'all', label: 'All day' },
              { key: 'morning', label: 'Morning' },
              { key: 'afternoon', label: 'Afternoon' },
              { key: 'evening', label: 'Evening' },
            ] as Array<{ key: HourWindow; label: string }>
          ).map(({ key, label }) => {
            const active = hourWindow === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setHourWindow(key)}
                aria-pressed={active}
                data-testid={`dashboard-filter-${key}`}
                className={
                  active
                    ? 'rounded-full border border-butler-400 bg-butler-100 px-2.5 py-1 text-butler-800 dark:border-butler-500 dark:bg-butler-900/60 dark:text-butler-100'
                    : 'rounded-full border border-hairline bg-white px-2.5 py-1 text-ink-soft hover:border-butler-300 hover:text-butler-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-butler-200'
                }
              >
                {label}
              </button>
            )
          })}
        </div>
      )}

      <div className="mm-card mt-2 p-3 dark:bg-slate-800" data-testid="dashboard-chart-pane">
        <div className="h-48 md:h-52">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={filteredHourly}
              margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0070AB" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#0070AB" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E9EBF2" />
              <XAxis
                dataKey="hour"
                tickFormatter={fmtHour}
                tick={{ fontSize: 11, fill: '#8697A8' }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#8697A8' }}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                labelFormatter={(h) => `Hour: ${fmtHour(h as number)}`}
                formatter={(value: number, name: string) =>
                  name === 'revenue' ? [fmtUSD(value), 'Revenue'] : [fmtNum(value), name]
                }
                contentStyle={{
                  borderRadius: 4,
                  border: '1px solid #E9EBF2',
                  fontSize: 12,
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#0070AB"
                strokeWidth={2}
                fill="url(#revGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Fork G Dashboard v2 mid-grid + drill-down. Mounted only when the
          hero is backed by real butler-db PG data — demo/mm_bridge modes
          don't have rollup tables to drill into, and firing those fetches
          would just noise the logs. Each sub-component owns its own fetch
          and renders an empty state on errors / gated-off backends. */}
      {stats.source === 'pg' ? (
        <div className="mt-6" data-testid="dashboard-deep-details">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-soft dark:text-slate-400">
            Deep details
          </div>
          <DashboardExtrasPane eventId={stats.event.event_id} authToken={authToken ?? null} />
          <DashboardDrillDown eventId={stats.event.event_id} authToken={authToken ?? null} />
        </div>
      ) : null}

      {/* V3 Page 01 — ExplainPanel. Flag-gated; mount only when we have a
          target so the aside doesn't render empty shell and trap focus on
          every dashboard paint. */}
      {flags.explainPanel ? (
        <ExplainPanel
          open={explainTarget !== null}
          onClose={closeExplainPanel}
          target={explainTarget}
          eventId={eventId}
          authToken={authToken ?? null}
          onNavigate={(route) => navigate(route)}
          onAskChat={(prompt) => {
            // Parent surfaces chat via a global event so Dashboard doesn't
            // reach into ChatContext — keeps the feature self-contained.
            const ev = new CustomEvent('butler:chat:prompt', { detail: { prompt } })
            window.dispatchEvent(ev)
            closeExplainPanel()
          }}
          onSaveCanvas={(widgetId) => {
            const ev = new CustomEvent('butler:canvas:save', { detail: { widgetId } })
            window.dispatchEvent(ev)
            closeExplainPanel()
          }}
        />
      ) : null}
    </section>
  )
}
